import React, { useCallback, useEffect, useRef, useState } from "react";
import { GetResidualFileBase64, ResidualFileGet } from "../../../../../api";
import ShowResidualPdfModal from "../../../../../components/modals/show-residual-pdf/show-residual-pdf";
import ExcelFile from "../../../../assets/svg/icons/common/excel-file";
import UploadIcon from "../../../../assets/svg/icons/common/upload";
import Select from "../../../../components/form/select";
import Spinner from "../../../../components/Loaders/Spinner";
import useCommonStore from "../../../../stores/common";
import useOrganizationStore from "../../../../stores/organization";
import AddResidualsModal from "./add-residuals-modal";
import styles from "./styles.module.scss";
import toast from "react-hot-toast";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const FileItem = ({ month, year, uploaded, hasPdf, onFileClick }) => (
  <div className={styles.file} onClick={() => onFileClick(month, year)}>
    <ExcelFile />
    <div className={styles.fileInfo}>
      <span className={styles.fileName}>{`${month}_${year}.xlsx`}</span>
      {hasPdf && <span className={styles.fileSize}>PDF available</span>}
    </div>
  </div>
);

const Residuals = () => {
  const dropZoneRef = useRef(null);
  const fileInputRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState(["2024_year"]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [openDocModal, setOpenDocModal] = useState(false);
  const [docModalFile, setDocModalFile] = useState(null);

  const expandDetails = useCommonStore((state) => state.expandDetails);
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [residualArray, setResidualArray] = useState([]);
  const [residualPdfData, setResidualPdfData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  const handleItemToggle = (id) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const populateLocalArray = (year) => {
    let ResidualFinalArray = [];
    if (ResidualFinalArray[year] === undefined) {
      ResidualFinalArray[year] = [];
    }
    for (let i = 1; i <= 12; i++) {
      ResidualFinalArray[year][i] = 0;
    }
    return ResidualFinalArray;
  };

  const fetchResiduals = useCallback(
    (year) => {
      let ResidualFinalArray = [];
      let ResidualFinalPDFArray = [];
      setLoading(true);

      ResidualFileGet({
        organization_id: selectedOrganizationId,
        year: year,
      })
        .then((response) => {
          if (response.success) {
            // ToDo: I have directly copied this as is from v1. Simplify this down the line.
            // There's an `empty` item which is being added to the array, not sure why.
            const residual_data = response.data;
            if (residual_data.length == 0) {
              ResidualFinalArray = populateLocalArray(year);
              ResidualFinalPDFArray = populateLocalArray(year);
            } else {
              residual_data.map((item) => {
                if (ResidualFinalArray[item.year] === undefined) {
                  ResidualFinalArray[item.year] = [];
                }
                if (ResidualFinalPDFArray[item.year] === undefined) {
                  ResidualFinalPDFArray[item.year] = [];
                }
                ResidualFinalArray[item.year][item.month] = item.uploaded;
                ResidualFinalPDFArray[item.year][item.month] =
                  item.converted_file ? 1 : 0;
              });
            }
            setResidualArray(ResidualFinalArray);
            setResidualPdfData(ResidualFinalPDFArray);
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
        });
    },
    [selectedOrganizationId],
  );

  useEffect(() => {
    fetchResiduals(new Date().getFullYear());
  }, [fetchResiduals]);

  const validateAndSetFile = (file) => {
    if (!file) return false;

    const fileType = file.name.split(".").pop().toLowerCase();
    if (fileType !== "xls" && fileType !== "xlsx") {
      toast.error("Please select a valid XLS/XLSX file");
      return false;
    }

    setFile(file);
    setUploadModalOpen(true);
    return true;
  };

  const handleFileDrop = (e) => {
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  useEffect(() => {
    if (dropZoneRef.current) {
      dropZoneRef.current.addEventListener("drop", handleFileDrop);
      return () => {
        dropZoneRef.current.removeEventListener("drop", handleFileDrop);
      };
    }
  }, []);

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
    event.target.value = "";
  };

  const handleChangeYear = (value) => {
    setYear(value);
    fetchResiduals(value);
  };

  const handleFileClick = useCallback(
    (month, year) => {
      setLoadingFile(true);
      GetResidualFileBase64({
        organization_id: selectedOrganizationId,
        month,
        year,
      })
        .then((response) => {
          if (response.success) {
            setDocModalFile(response);
            setOpenDocModal(true);
          } else {
            setOpenDocModal(false);
            setDocModalFile([]);
          }
          setLoadingFile(false);
        })
        .catch((error) => {
          console.error("Error loading file:", error);
          setLoadingFile(false);
        });
    },
    [selectedOrganizationId],
  );

  return (
    <div
      className={`${styles.content} ${expandDetails ? styles.expanded : ""}`}
    >
      <div className={styles.fileList}>
        <div className={styles.filterContainer}>
          <Select
            options={(() => {
              const currentYear = new Date().getFullYear();
              const years = [];
              for (let i = currentYear; i >= currentYear - 15; i--) {
                years.push({ value: i.toString(), label: i.toString() });
              }
              return years;
            })()}
            value={{
              value: year,
              label: year,
            }}
            onChange={handleChangeYear}
            placeholder="Select year"
          />
        </div>

        {loading && (
          <div className={styles.loading}>
            <Spinner />
          </div>
        )}

        {!loading && residualArray[year] && (
          <div className={styles.monthFiles}>
            {MONTHS.map((month, index) => {
              const isUploaded = residualArray[year][index + 1] === 1;
              const hasPdf = residualPdfData[year]?.[index + 1] === 1;

              return isUploaded ? (
                <FileItem
                  key={`${month}-${year}`}
                  month={month}
                  year={year}
                  uploaded={isUploaded}
                  hasPdf={hasPdf}
                  onFileClick={() => handleFileClick(month, year)}
                />
              ) : null;
            })}
          </div>
        )}

        {!loading &&
          (!residualArray[year] ||
            Object.values(residualArray[year] || {}).every((v) => v === 0)) && (
            <div className={styles.noData}>
              <p>No data to display</p>
            </div>
          )}
      </div>

      <div className={styles.uploadSection}>
        <h2>Upload Residuals</h2>
        <div
          className={styles.dropZone}
          onClick={() => fileInputRef.current.click()}
          ref={dropZoneRef}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".xls,.xlsx"
            style={{ display: "none" }}
          />
          <div className={styles.uploadIcon}>
            <UploadIcon />
          </div>
          <div className={styles.uploadText}>
            <p>
              Upload documents by drag and drop or{" "}
              <span className={styles.uploadButton}>click to upload</span>
            </p>
            <p className={styles.fileTypes}>xlsx, xls</p>
          </div>
        </div>
      </div>

      <AddResidualsModal
        onClose={() => {
          setUploadModalOpen(false);
          fetchResiduals(year);
        }}
        open={uploadModalOpen}
        file={file}
        clearFile={() => {
          setFile(null);
          fileInputRef.current.value = "";
        }}
      />

      {openDocModal && (
        <ShowResidualPdfModal
          isOpen={openDocModal}
          close={() => setOpenDocModal(false)}
          file={docModalFile}
          fileType={"pdf"}
        />
      )}
    </div>
  );
};

export default Residuals;
